/** Post-completion summary for `entity_export` background tasks: record /
 *  group counts, format, and the artifact name + size from the task result.
 *  (The artifact itself is downloadable from the task's Artifacts list.)
 *  When the export detected duplicate external IDs it also renders the
 *  ambiguity warning with the colliding records, linked to the dashboard. */
import { Alert, Box, Link, Stack, Typography } from '@mui/material';
import { Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';

import * as bgtasksApi from '@/api/bgtasks';

interface DuplicateRecord {
  id: number;
  dashboard_url?: string | null;
}

interface DuplicateGroup {
  entity: string;
  custom_id: string;
  records: DuplicateRecord[];
}

interface DuplicateSummary {
  group_count: number;
  record_count: number;
  groups: DuplicateGroup[];
  truncated: boolean;
  warning: string | null;
}

interface ExportedFile {
  tenant: string;
  artifact: string;
  records: number;
  source_id?: string;
}

interface SkippedTenant {
  id: number;
  code: string;
  name: string;
}

interface EntityExportResult {
  records?: number;
  groups?: number;
  artifact?: string | null;
  size?: number;
  format?: string;
  duplicates?: DuplicateSummary;
  /** per_tenant mode: one artifact per operator. */
  files?: ExportedFile[];
  /** Multi-tenant mode: operators skipped for lack of a default import key. */
  skipped_tenants?: SkippedTenant[];
}

function DuplicatesWarning({ duplicates }: { duplicates: DuplicateSummary }) {
  return (
    <Alert severity="warning" sx={{ mt: 1 }}>
      <Typography variant="body2" gutterBottom>
        {duplicates.group_count.toLocaleString()} external ID(s) appear more than once in
        this file ({duplicates.record_count.toLocaleString()} records).{' '}
        {duplicates.warning ??
          'If this file is imported back, the result for these records is ambiguous.'}
      </Typography>
      <Stack spacing={0.25}>
        {duplicates.groups.map((group) => (
          <Typography key={`${group.entity}:${group.custom_id}`} variant="body2">
            <code>
              {group.entity} {group.custom_id}
            </code>
            :{' '}
            {group.records.map((record, i) => (
              <Fragment key={record.id}>
                {i > 0 && ', '}
                {record.dashboard_url ? (
                  <Link href={record.dashboard_url} target="_blank" rel="noopener noreferrer">
                    #{record.id}
                  </Link>
                ) : (
                  <>#{record.id}</>
                )}
              </Fragment>
            ))}
          </Typography>
        ))}
        {duplicates.truncated && (
          <Typography variant="body2" color="text.secondary">
            … more duplicate groups omitted; the full list is in the task log.
          </Typography>
        )}
      </Stack>
    </Alert>
  );
}

export function EntityExportResultPanel({ taskId }: { taskId: string }) {
  const query = useQuery({
    queryKey: ['bgtask', taskId, 'result'],
    queryFn: () => bgtasksApi.result(taskId),
    staleTime: Infinity, // a finished export's result is immutable
    retry: false,
  });
  const result = query.data?.result as unknown as EntityExportResult | undefined;
  if (!result) return null;

  const parts = [
    result.records != null ? `${result.records.toLocaleString()} record(s)` : null,
    result.groups != null ? `${result.groups.toLocaleString()} group(s)` : null,
    result.format ? result.format.toUpperCase() : null,
    result.size != null ? `${(result.size / 1024).toFixed(1)} KB` : null,
  ].filter((p): p is string => p !== null);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Export result
      </Typography>
      <Stack spacing={0.5}>
        {parts.length > 0 && <Typography variant="body2">{parts.join(' · ')}</Typography>}
        {result.files && result.files.length > 0 ? (
          <>
            <Typography variant="body2" color="text.secondary">
              One file per operator (download them from the Artifacts list below):
            </Typography>
            {result.files.map((f) => (
              <Typography key={f.artifact} variant="body2" color="text.secondary">
                <code>{f.artifact}</code> — {f.tenant}, {f.records.toLocaleString()}{' '}
                record(s)
                {f.source_id ? (
                  <>
                    {' '}
                    · source <code>{f.source_id}</code>
                  </>
                ) : null}
              </Typography>
            ))}
          </>
        ) : (
          result.artifact && (
            <Typography variant="body2" color="text.secondary">
              Artifact: <code>{result.artifact}</code> (download it from the Artifacts list
              below)
            </Typography>
          )
        )}
      </Stack>
      {result.skipped_tenants && result.skipped_tenants.length > 0 && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {result.skipped_tenants.length.toLocaleString()} operator(s) skipped — no default
          import key:{' '}
          {result.skipped_tenants.map((t) => `${t.name} (${t.code})`).join(', ')}. Their
          records are not in the export; details are in the task log.
        </Alert>
      )}
      {result.duplicates && result.duplicates.group_count > 0 && (
        <DuplicatesWarning duplicates={result.duplicates} />
      )}
    </Box>
  );
}
